import React, { useCallback } from 'react';
import colors from '../../../constants/colors.constants';
import Typo from '../../../components/atom/Typo';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Accordion, AccordionItem } from '@szhsin/react-accordion';
import { fetchAfterNoticeList } from '../../../apis/after-notice';
import useAuthStore from '../../../store/useAuthStore';
import arrowDown from '../../../assets/svgs/arrow-down.svg';
import useLanguageCodeStore from '../../../store/useLanguageCodeStore';
import { InnerHtmlWrap } from '../../../components/components/InnerHtmlWrap';

export interface AfterNoticePageProps {}

function AfterNoticePage(props: AfterNoticePageProps) {
  const [searchParams] = useSearchParams();
  const ids = searchParams.get('ids');
  const { partner } = useAuthStore();
  const { languageCode } = useLanguageCodeStore();

  const getAfterNoticeIds = useCallback(() => {
    if (ids) {
      try {
        const parsedIds = JSON.parse(ids);
        if (Array.isArray(parsedIds)) {
          return parsedIds as number[];
        }
        return [parsedIds] as number[];
      } catch (e) {
        return [];
      }
    }
    return [];
  }, [ids]);

  const { data } = useQuery({
    queryKey: [`FETCH_AFTER_NOTICES`],
    queryFn: async () => {
      return fetchAfterNoticeList({
        partnerId: partner?.id || 0,
        ids: getAfterNoticeIds(),
        languageCode: languageCode !== 'ko' ? languageCode : undefined,
      });
    },
    retry: false,
    enabled: !!partner && getAfterNoticeIds().length > 0,
    refetchOnWindowFocus: false,
  });

  const getTitle = () => {
    if (!languageCode || languageCode === 'ko') {
      return '시술 후 유의사항';
    }
    if (data?.rows && data?.rows.length > 0) {
      return data.rows.map((row) => row.title).join(', ');
    }
    return '';
  };

  return (
    <div className="h-full flex flex-col">
      <div
        className="min-h-[48px] flex items-center justify-center relative"
        style={{
          borderBottom: `1px solid ${colors.defaultBorder}`,
        }}
      >
        <div
          style={{
            padding: '5px',
            cursor: 'pointer',
            position: 'absolute',
            left: 16,
          }}
        ></div>
        <Typo type="b-16">{getTitle()}</Typo>
      </div>
      <div
        className="flex-1 overflow-y-auto"
        style={{
          paddingBottom: 20,
        }}
      >
        {data?.rows.map(
          ({ id, afterNoticeItems, afterNoticeType, body = '' }) => {
            if (afterNoticeType === 'BODY' && body) {
              return (
                <InnerHtmlWrap
                  dangerouslySetInnerHTML={{ __html: body }}
                  className="ck ck-content"
                />
              );
            }
            if (afterNoticeType === 'ITEM') {
              return (
                <Accordion key={id} allowMultiple transition>
                  {afterNoticeItems.map(({ title, id, description }) => {
                    return (
                      <AccordionItem
                        initialEntered
                        key={id}
                        header={({ state }) => {
                          const { isEnter } = state;
                          return (
                            <div className="flex items-center justify-between cursor-pointer">
                              {title}
                              <img
                                className={`ml-auto transition-transform duration-200 ease-out ${
                                  isEnter && 'rotate-180'
                                }`}
                                src={arrowDown}
                                alt="Chevron"
                              />
                            </div>
                          );
                        }}
                        className="px-5 py-4 w-full border-b"
                        style={{
                          borderColor: colors.defaultBorder,
                        }}
                        buttonProps={{
                          className: 'accordion-btn',
                        }}
                        contentProps={{ className: 'itemContent' }}
                        panelProps={{ className: 'itemPanel' }}
                      >
                        <div
                          className="whitespace-pre-wrap py-4 bg-gray-100"
                          style={{
                            fontSize: 14,
                            lineHeight: 1.5,
                            borderRadius: 8,
                          }}
                        >
                          <InnerHtmlWrap
                            dangerouslySetInnerHTML={{ __html: description }}
                            className="ck ck-content"
                          />
                        </div>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              );
            }
            return null;
          },
        )}
      </div>
    </div>
  );
}

export default AfterNoticePage;
